// Store selection: OPFS when the browser has it and enough free space; bounded memory only for small files.
import { TransferFailure, MESSAGES } from '../errors.js';
import { createMemoryStore, DEFAULT_MEMORY_LIMIT } from './memory-store.js';
import { activeKeys, createOpfsStore, freeBytes, listCacheEntries, opfsSupported, planSweep, storeKey, sweepOpfs } from './opfs-store.js';

export { activeKeys, createMemoryStore, createOpfsStore, listCacheEntries, opfsSupported, planSweep, sweepOpfs, DEFAULT_MEMORY_LIMIT };

/**
 * @returns {Promise<{ store: object, candidates: number[] }>}
 */
export async function chooseStore(manifest, { policy = {}, memoryLimit = DEFAULT_MEMORY_LIMIT, opfs = opfsSupported() } = {}) {
  if (opfs) {
    // Age check BEFORE opening: an expired entry (even for this file) is deleted rather than refreshed by the open.
    // The resume target itself is spared from quota eviction; keys active on this page are never touched.
    const key = await storeKey(manifest);
    await sweepOpfs({ ttlSeconds: policy.cacheTtlSeconds, quotaBytes: policy.cacheQuotaBytes, keep: key }).catch(() => {});
    const store = await createOpfsStore(manifest); // holds the cross-page lease from here on
    let candidates, free;
    try {
      candidates = await store.open();
      free = await freeBytes();
    } catch (e) { store.release(); throw e; } // a failed store never keeps its key leased
    const stored = candidates.reduce((n, i) => n + manifest.pieces[i].length, 0);
    if (free == null || free >= manifest.sizeBytes - stored) return { store, candidates };
    await store.remove();
    if (manifest.sizeBytes <= memoryLimit) return { store: createMemoryStore(manifest, { limitBytes: memoryLimit }), candidates: [] };
    throw new TransferFailure('storage', MESSAGES.storage);
  }
  if (manifest.sizeBytes <= memoryLimit) return { store: createMemoryStore(manifest, { limitBytes: memoryLimit }), candidates: [] };
  throw new TransferFailure('unsupported', MESSAGES.unsupported);
}

/**
 * Can this browser hold `sizeBytes` locally for a verified transfer? OPFS with enough reported free space, or a
 * small file within the bounded memory fallback. Used to choose the native download up front for large files.
 */
export async function canStoreLocally(sizeBytes, { memoryLimit = DEFAULT_MEMORY_LIMIT, opfs = opfsSupported() } = {}) {
  if (!Number.isFinite(sizeBytes) || sizeBytes < 0) return false;
  if (opfs) {
    const free = await freeBytes();
    if (free == null || free >= sizeBytes) return true;
  }
  return sizeBytes <= memoryLimit;
}
